<template>
	<div
		class="products__item"
		:class="{
			'products__item--special': product.special,
			'products__item--limited': stock > 0,
      'products__item--exclusive': product.isCustomerExclusive
		}"
	>
		<script
			v-if="showProductSchema"
			type="application/ld+json"
			v-html="productSchemaJsonLd"
		></script>

		<img
			class="products__item-label"
			v-if="productLabel"
			:src="productLabel"
			loading="lazy"
		>

		<a
      v-if="product.special"
			class="products__item-large-image"
      :class="{
        'products__item-large-image--exclusive': product.isCustomerExclusive
      }"
			href="javascript:void(0)"
			@click.prevent="onClick"
		>
			<img
				ref="largeImage"
				class="products__item-large-image-element"
				data-object-fit
				:src="product.imageSpecial || product.imageMood || product.image"
				:alt="product.name"
				loading="lazy"
			>
		</a>
    <div
      v-if="product.special && product.isCustomerExclusive"
      class="products__item-large-image-exclusive"
      v-html="$t('shoplist.product.customerExclusive')"
    />
		<div class="products__item-container">
			<a
        v-if="!product.special"
				class="products__item-image"
        :class="{
          'products__item-image--exclusive': product.isCustomerExclusive
        }"
				href="javascript:void(0)"
				@click.prevent="onClick"
			>
				<figure class="products__item-image-wrapper">
					<img
						class="products__item-image-element"
						:class="{
							'products__item-image-element--wide': product.imageMood && !imageError
						}"
						:src="product.imageMood || product.image"
						:alt="product.name"
						loading="lazy"
						@error="onImageError"
					>
				</figure>
        <div
          v-if="product.isCustomerExclusive"
          class="products__item-image-exclusive"
          v-html="$t('shoplist.product.customerExclusive')"
        />
			</a>
			<div class="products__item-content">
				<div class="products__item-name">
					<a
						href="javascript:void(0)"
						v-html="product.name"
						@click.prevent="onClick"
					/>
				</div>
				<div
					class="products__item-desc"
					v-html="product.shortDescription || product.description"
				/>

				<div
					class="products__item-limited"
					v-if="hasFeature('FEATURE_LIMITEDSTOCK') && stock > 0"
					v-html="$t('shoplist.product.limitedStock')"
				/>

				<!-- current variant selector -->
				<div
					class="products__item-variants"
					v-if="product.variants.length > 1"
				>
					<select
						class="products__item-variants-select"
						v-model="selectedVariant"
					>
						<option
							v-for="(variant, index) in availableVariants"
							:key="variant.code"
							:value="index"
							v-html="variant.name"
						/>
					</select>
				</div>

				<div
					v-if="price || !product.isConfigurable || priceReduction"
					class="products__item-price"
				>
					<strike
						class="products__item-price-old"
						v-if="originalPrice"
						v-html="$options.filters.fwgpricecent(originalPrice, 'short')"
					/>
					<span
						class="products__item-price-current"
						v-html="$options.filters.fwgpricecent(price, 'short')"
					/>
					<div
						v-if="co2Compensation"
						class="products__item-price-co2"
						v-html="$t('shoplist.product.co2Compensation', { amount: $options.filters.fwgpricecent(co2Compensation, 'short') })"
					/>
				</div>
				<div
					v-if="priceReduction"
					class="products__item-price-reduction"
					v-html="priceReduction"
				/>

				<template v-if="hasBasket && !publicMode && !menuMode && !disabled">
					<div
						class="products__item-cart"
						:class="{
							'products__item-cart--editable': product.isEditable
						}"
					>
						<div
							class="products__item-cart-message"
							ref="message"
							:class="{'products__item-cart-message--added': addedToCart}"
							@click="onCloseMessage"
							v-html="$t('shoplist.product.successCart')"
						/>
						<QuantityCounter
							v-if="!product.isEditable"
							:quantity="quantity"
							base-class="products__item-quantity"
							@sub="onSub()"
							@add="onAdd()"
						/>
						<Button
							class="products__item-configure-button"
							@click.prevent="onConfig()"
							level="outline"
							type="button"
							:text="$t('shoplist.product.configButton')"
							:disabled="locked"
							small
							v-if="product.isConfigurable"
						/>
						<button
							class="products__item-cart-button"
							@click="onCart()"
							:disabled="locked"
							v-else
						>
							<SVGBag/>
						</button>
					</div>
				</template>
				<template v-else-if="!menuMode && !disabled">
					<div class="products__item-cart">
						<Button
							class="products__item-public-button"
							@click.prevent="$emit('orderbox')"
							level="outline"
							type="button"
							:text="$t('shoplist.product.startOrder')"
							small
						/>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import scrrroll from 'scrrroll';
import genericMixin from '@/mixins/GenericVueMixin';
import QuantityCounter from '@/components/QuantityCounter';
import Button from '@/components/Button';
import MixinProductLabels from "@/mixins/MixinProductLabels";

export default {
	mixins: [
		genericMixin,
		MixinProductLabels,
	],
	components: {
		QuantityCounter,
		Button,
	},
	props: {
		product: Object,
		locked: Boolean,
		disabled: Boolean,
	},
	data() {
		return {
			quantity: 1,
			selectedVariant: this.product.variants.findIndex(v => v.code == this.product.selectedVariant),
			addedToCart: false,
			imageError: false,
		};
	},
	computed: {
		...mapGetters('cart', [
			'hasBasket',
		]),
		...mapGetters('productlist', [
			'menuMode',
			'publicMode',
		]),
		price() {
			if (this.product.variants.length && this.product.variants.length > this.selectedVariant) {
				return this.product.variants[this.selectedVariant].price;
			}
			return this.product.price;
		},
		originalPrice() {
			let originalPrice = this.product.originalPrice;

			if (this.product.variants.length && this.product.variants.length > this.selectedVariant) {
				originalPrice = this.product.variants[this.selectedVariant].originalPrice;
			}
			return !!originalPrice && this.price < originalPrice && originalPrice;
		},
		co2Compensation() {
			return this.hasFeature('FEATURE_PRODUCT_CO2_REMOVAL') && this.variant?.co2?.amount
		},
		priceReduction() {
			if (this.originalPrice) {
				if (this.product.discountInPercent) {
					return `-${Math.floor(100 / this.originalPrice * (this.originalPrice - this.price))}%`;
				}
				else {
					return `-${this.$options.filters.fwgpricecent((this.originalPrice - this.price), 'short')} CHF`;
				}
			}
			return false;
		},
		variant() {
			if (this.product.variants.length) {
				if (this.product.variants.length > this.selectedVariant) {
					return this.product.variants[this.selectedVariant];
				}
				return this.product.variants[0];
			}
			else {
				return false;
			}
		},
		availableVariants() {
			// only show variants that have stock
			return this.product.variants.filter(v => v.stock != 0)
		},
		showProductSchema() {
			return this.publicMode;
		},
		productSchemaJsonLd() {
			if (!this.showProductSchema) {
				return '';
			}

			const image = this.product.imageSpecial || this.product.imageMood || this.product.image;
			const schema = {
				'@context': 'https://schema.org',
				'@type': 'Product',
				name: this.$options.filters.stripHtml(this.product.name),
				description: this.$options.filters.stripHtml(this.product.shortDescription || this.product.description || ''),
				sku: this.variant?.code || this.product.code,
				offers: {
					'@type': 'Offer',
					priceCurrency: 'CHF',
					price: this.$options.filters.fwgpricecent(this.price || 0, 'short'),
					availability: this.stock !== 0
						? 'https://schema.org/InStock'
						: 'https://schema.org/OutOfStock'
				}
			};

			if (image) {
				schema.image = [image];
			}

			if (this.product.brand) {
				schema.brand = {
					'@type': 'Brand',
					name: this.$options.filters.stripHtml(this.product.brand)
				};
			}

			return JSON.stringify(schema);
		},
		stock() {
			if (this.product.variants.length && this.product.variants.length > this.selectedVariant) {
				if (!isNaN(this.product.variants[this.selectedVariant].stock)) {
					return this.product.variants[this.selectedVariant].stock;
				}
			}
			return this.product.stock;
		}
	},
	watch: {
		locked: function (value) {
			if (value) {
				this.onAddedToCart();
			}
		}
	},
	methods: {
		onImageError() {
			this.imageError = true;
		},
		onClick(e) {
			this.$emit('click', this.product.code);
		},
		onSub() {
			this.quantity = Math.max(this.quantity - 1, 1);
			this.$emit('quantitychange', this.quantity);
		},
		onAdd() {
			this.quantity = this.quantity + 1;
			this.$emit('quantitychange', this.quantity);
		},
		onConfig() {
			const item = {
				...this.product,
				price: this.price,
				variant: this.variant.name,
				selectedVariant: this.variant.code,
			}

			this.$emit('config', item);
		},
		onCart() {
			this.addedToCart = false;

			const item = {
				quantity: this.quantity,
				productCode: this.product.code,
				variantCode: this.variant.code
			}
			if (this.product.isEditable) {
				item.forceCopy = true
			}

			this.$emit('addToCart', item);

			this.$nextTick(() => {
				this.quantity = 1;
			});
		},
		onAddedToCart() {
			this.addedToCart = true;

			if (this.$refs.message.getBoundingClientRect().top < 0) {
				scrrroll.to(this.$refs.message, 300);
			}

			clearTimeout(this.messageTimeout);
			this.messageTimeout = setTimeout(() => {
				this.addedToCart = false;
			}, 6000);
		},
		onCloseMessage(e) {
			clearTimeout(this.messageTimeout);
			this.addedToCart = false;
		}
	}
}
</script>

<style>

</style>
